import type { Substance } from '../types';

export const caffeine: Substance = {
  id: 'caffeine',
  displayName: 'Caffeine',
  emoji: '☕',
  aliases: ['caffeine', 'coffee', 'espresso'],
  substanceClass: 'stimulant',
  mechanismSummary:
    'Non-selective adenosine receptor antagonist. Blocking A1/A2A prevents adenosine from suppressing ' +
    'neuronal firing, producing alertness. Caffeine is a comparatively weak (micromolar-affinity) ligand ' +
    'at these receptors — which is exactly why effective doses are hundreds of milligrams rather than the ' +
    'micrograms typical of high-affinity drugs.',
  pk: {
    route: 'oral',
    ka: 1.5,
    ke: 0.1386,
    vd: 42,
    bioavailability: 1.0,
    molecularWeightGMol: 194.19,
    brainPlasmaRatio: 0.9,
  },
  doseRange: { min: 25, max: 400, default: 95, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'A1',
      kiNM: 10700,
      action: 'antagonist',
      citation:
        'Micromolar, not nanomolar, affinity — Fredholm BB et al. (1999) Pharmacol Rev 51:83-133. Human ' +
        'A1 figure; rodent studies report up to ~44,000 nM, so treat as an order-of-magnitude estimate.',
    },
    {
      receptor: 'A2A',
      kiNM: 2400,
      action: 'antagonist',
      citation:
        'Approximate central estimate; reported values span roughly 2.4-23 uM across assays. Fredholm et ' +
        'al. (1999) Pharmacol Rev 51:83-133; Jacobson KA, Gao ZG et al. (2022) Br J Pharmacol 179:3496-3512.',
    },
  ],
  colorPalette: { primary: '#ff9930', secondary: '#ffcc00', particleSpeedMultiplier: 1.6 },
};

export const nicotine: Substance = {
  id: 'nicotine',
  displayName: 'Nicotine',
  emoji: '💨',
  aliases: ['nicotine', 'cigarette', 'vape', 'tobacco'],
  substanceClass: 'stimulant',
  mechanismSummary:
    'Agonist at nicotinic acetylcholine receptors. Beyond the brain, nAChRs are the relay of every ' +
    'autonomic ganglion, so nicotine acts on the peripheral nervous system as directly as on the CNS — ' +
    'which is why it raises heart rate and blood pressure alongside its cognitive effects.',
  pk: {
    route: 'oral',
    ka: 1.54,
    ke: 0.3466,
    vd: 175,
    bioavailability: 0.35,
    molecularWeightGMol: 162.23,
    brainPlasmaRatio: 1.0,
  },
  doseRange: { min: 0.5, max: 4, default: 2, step: 0.1, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'nAChR',
      kiNM: 1,
      action: 'agonist',
      citation:
        'Xiao Y, Kellar KJ (2004) J Pharmacol Exp Ther 310:98-107. PMID: 15016836. ~1 nM is the consensus ' +
        'figure for a4b2; individual studies report ~0.5-5 nM depending on preparation and radioligand.',
    },
  ],
  colorPalette: { primary: '#ffaa22', secondary: '#ff8800', particleSpeedMultiplier: 1.4 },
};

export const cocaine: Substance = {
  id: 'cocaine',
  displayName: 'Cocaine',
  emoji: '❄️',
  aliases: ['cocaine', 'coke'],
  substanceClass: 'stimulant',
  mechanismSummary:
    'Blocks the dopamine, noradrenaline and serotonin transporters, so released transmitter is not cleared ' +
    'from the synapse. It is also a local anaesthetic — it blocks voltage-gated sodium channels, which is ' +
    'why it numbs tissue it touches and why it is cardiotoxic at high doses.',
  pk: {
    route: 'oral',
    ka: 1.6,
    ke: 0.77,
    vd: 140,
    bioavailability: 0.3,
    molecularWeightGMol: 303.35,
    brainPlasmaRatio: 1.2,
  },
  doseRange: { min: 10, max: 150, default: 50, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'DAT',
      kiNM: 640,
      action: 'reuptake-inhibitor',
      citation:
        'Volkow ND et al. (1999) "Methylphenidate and cocaine have a similar in vivo potency to block ' +
        'dopamine transporters in the human brain." PMID: 10403500.',
    },
    {
      receptor: 'SERT',
      kiNM: 300,
      action: 'reuptake-inhibitor',
      citation: 'Approximate; cocaine is a non-selective monoamine reuptake blocker. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'NET',
      kiNM: 780,
      action: 'reuptake-inhibitor',
      citation: 'Approximate; see NIMH PDSP Ki Database, https://pdsp.unc.edu/databases/kidb.php.',
    },
    {
      receptor: 'NAV',
      kiNM: 100000,
      action: 'antagonist',
      citation:
        'Functional local-anaesthetic action rather than a classic Ki — cocaine blocks voltage-gated ' +
        'sodium channels in the tens-to-hundreds of micromolar range. Order-of-magnitude estimate.',
    },
  ],
  colorPalette: { primary: '#ffffff', secondary: '#88ddff', particleSpeedMultiplier: 2.2 },
};

export const amphetamine: Substance = {
  id: 'amphetamine',
  displayName: 'Amphetamine',
  emoji: '⚡',
  aliases: ['amphetamine', 'speed', 'adderall', 'dexamphetamine'],
  substanceClass: 'stimulant',
  mechanismSummary:
    'Unlike cocaine, amphetamine is a transporter substrate rather than a simple blocker: it is carried ' +
    'into the neuron and reverses transporter direction, actively pumping dopamine and noradrenaline out ' +
    'into the synapse. That efflux mechanism is why its effects are larger and longer than reuptake ' +
    'blockade alone would predict.',
  pk: {
    route: 'oral',
    ka: 1.1,
    ke: 0.0693,
    vd: 350,
    bioavailability: 0.9,
    molecularWeightGMol: 135.21,
    brainPlasmaRatio: 1.3,
  },
  doseRange: { min: 5, max: 40, default: 15, step: 1, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'DAT',
      kiNM: 100,
      action: 'reuptake-inhibitor',
      citation:
        'Approximate. Amphetamine acts as a DAT substrate causing reverse transport, so a simple Ki ' +
        'understates its action — the value here stands in for that releasing effect. See Sitte HH, ' +
        'Freissmuth M (2015) Trends Pharmacol Sci 36:41-50.',
    },
    {
      receptor: 'NET',
      kiNM: 40,
      action: 'reuptake-inhibitor',
      citation: 'Approximate; amphetamine is more potent at NET than DAT. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ff4466', secondary: '#ffaa00', particleSpeedMultiplier: 2.4 },
};

export const methylphenidate: Substance = {
  id: 'methylphenidate',
  displayName: 'Methylphenidate',
  emoji: '💊',
  aliases: ['methylphenidate', 'ritalin', 'concerta'],
  substanceClass: 'stimulant',
  mechanismSummary:
    'Blocks the dopamine and noradrenaline transporters. Its in vivo potency at DAT is comparable to ' +
    'cocaine, but its much slower rise in brain concentration when taken orally is a large part of why ' +
    'its clinical profile differs so sharply.',
  pk: {
    route: 'oral',
    ka: 1.2,
    ke: 0.231,
    vd: 175,
    bioavailability: 0.3,
    molecularWeightGMol: 233.31,
    brainPlasmaRatio: 1.1,
  },
  doseRange: { min: 5, max: 60, default: 20, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'DAT',
      kiNM: 390,
      action: 'reuptake-inhibitor',
      citation: 'Volkow ND et al. (1999) PMID: 10403500 — reports methylphenidate Ki 390 nM vs cocaine 640 nM.',
    },
    {
      receptor: 'NET',
      kiNM: 640,
      action: 'reuptake-inhibitor',
      citation: 'Approximate; NIMH PDSP Ki Database, https://pdsp.unc.edu/databases/kidb.php.',
    },
  ],
  colorPalette: { primary: '#ffbb44', secondary: '#ff7733', particleSpeedMultiplier: 1.7 },
};

export const mdma: Substance = {
  id: 'mdma',
  displayName: 'MDMA',
  emoji: '💗',
  aliases: ['mdma', 'ecstasy', 'molly'],
  substanceClass: 'stimulant',
  mechanismSummary:
    'A serotonin-releasing agent: it enters the neuron via SERT and reverses it, flooding the synapse with ' +
    'serotonin, with smaller dopamine and noradrenaline release. The resulting serotonin depletion is why ' +
    'the after-effects tend to be low mood for days.',
  pk: {
    route: 'oral',
    ka: 1.0,
    ke: 0.0866,
    vd: 400,
    bioavailability: 0.75,
    molecularWeightGMol: 193.25,
    brainPlasmaRatio: 1.2,
  },
  doseRange: { min: 40, max: 150, default: 90, step: 5, unit: 'mg', displayScale: 1 },
  bindings: [
    {
      receptor: 'SERT',
      kiNM: 240,
      action: 'reuptake-inhibitor',
      citation:
        'Approximate; MDMA is a SERT substrate/releaser rather than a pure blocker, so the value stands in ' +
        'for its releasing action. Verrico CD et al. (2007) Psychopharmacology 189:489-503.',
    },
    {
      receptor: 'NET',
      kiNM: 460,
      action: 'reuptake-inhibitor',
      citation: 'Approximate; NIMH PDSP Ki Database.',
    },
    {
      receptor: '5HT2A',
      kiNM: 5000,
      action: 'partial-agonist',
      citation: 'Weak direct affinity; most of MDMA\'s serotonergic effect is release-mediated. PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ff77cc', secondary: '#ffdd55', particleSpeedMultiplier: 1.9 },
};

export const STIMULANTS = [caffeine, nicotine, cocaine, amphetamine, methylphenidate, mdma];
